import React from 'react';
import { CandidateFormFields } from 'pages/CandidatesRanking/constants';
import { RequirementSettingItem } from './RequirementSettingItem';
import { Button } from 'system/atoms';
import AddIcon from '@mui/icons-material/Add';
import { Box, Divider } from '@mui/material';
import { FormikErrors } from 'formik';
import { AnalysisRequirementInputWithExtras } from 'modules/analyse/types/AnalysisRequirementInputWithExtras';
import { defaultNewRequirement } from 'modules/analyse/constants/defaultNewRequirement';
type Props = {
  setFieldValue: (field: string, value: any) => void;
  setFieldError: (field: string, value: string | undefined) => void;
  value: AnalysisRequirementInputWithExtras[];
  onFormSubmitted?: () => void;
  errors?: FormikErrors<AnalysisRequirementInputWithExtras>[];
  isEditable?: boolean;
  candidateBatchId?: string;
  candidateBatchCandidateCount?: number | null;
};

export const RequirementsSettings: React.FC<Props> = ({
  setFieldValue,
  setFieldError,
  value,
  errors,
  candidateBatchId,
  candidateBatchCandidateCount
}) => {
  const deleteRequirement = (key: string) => {
    const newRequirements = value.filter((item) => item.key !== key);
    setFieldValue(CandidateFormFields.requirements, newRequirements);
  };

  const addRequirement = () => {
    const newRequirements = [...value];

    newRequirements.push(defaultNewRequirement());
    setFieldValue(CandidateFormFields.requirements, newRequirements);
  };

  const handleAddMultipleRecommendedRequirements = (
    index: number,
    requirements: string[]
  ) => {
    const newRequirements = [...value];

    // Add new requirements below the specified index
    const newRequirementsToInsert = requirements.map((content, i) => {
      if (i === 0) {
        return {
          ...newRequirements[index],
          content
        };
      }

      return defaultNewRequirement({
        isRecommendationDismissed: true,
        content
      });
    });

    // Replace the current requirement and insert additional ones after it
    newRequirements.splice(index, 1, ...newRequirementsToInsert);
    setFieldValue(CandidateFormFields.requirements, newRequirements);
  };

  const handleUndoMultipleRequirementsAdjustment = (
    index: number,
    originalContent: string,
    requirements: string[]
  ) => {
    const newRequirements = [...value];

    // Revert the requirement at the specified index to its original content
    newRequirements[index] = {
      ...newRequirements[index],
      content: originalContent
    };

    // Find and remove requirements that match the requirements string array
    // We need to filter out requirements whose content matches any of the strings in the requirements array
    const filteredRequirements = newRequirements.filter(
      (requirement, reqIndex) => {
        // Keep the requirement at the original index (already reverted above)
        if (reqIndex === index) {
          return true;
        }

        // Remove requirements whose content matches any string in the requirements array
        return !requirements.includes(requirement.content);
      }
    );

    setFieldValue(CandidateFormFields.requirements, filteredRequirements);
  };

  return (
    <>
      {value.map((requirement, index) => (
        <Box key={requirement.key} sx={{ mb: '15px' }}>
          <Box my="30px">
            <RequirementSettingItem
              index={index}
              candidateBatchId={candidateBatchId}
              candidateBatchCandidatesCount={candidateBatchCandidateCount}
              requirementMetCandidatesCount={
                requirement.requirementMetCandidatesCount
              }
              recommendationCategory={requirement.recommendationCategory}
              recommendationText={requirement.recommendationText}
              isRecommendationDismissed={requirement.isRecommendationDismissed}
              key={requirement.key}
              content={requirement.content}
              weight={requirement.weight}
              setFieldValue={setFieldValue}
              setFieldError={setFieldError}
              onAddMultipleRequirements={
                handleAddMultipleRecommendedRequirements
              }
              onUndoMultipleRequirementsAdjustment={
                handleUndoMultipleRequirementsAdjustment
              }
              deleteRequirement={() => deleteRequirement(requirement.key)}
              fieldName={CandidateFormFields.requirements}
              isMandatory={requirement.isMandatory}
              error={!!errors?.[index]?.content}
            />
          </Box>
          <Divider key={`divider-${requirement.id || index}`} />
        </Box>
      ))}
      <Box alignSelf="start" my="15px">
        <Button
          variant="tertiary"
          startIcon={<AddIcon />}
          onClick={addRequirement}
        >
          Add requirement
        </Button>
      </Box>
      <Divider />
    </>
  );
};
